import { Box, Text, Code, IconButton } from "@radix-ui/themes";
import Modal from "@/components/ui/Modal";
import { HelpCircle } from "lucide-react";
import { useState } from "react";

const PayloadHelpContent = () => {
  return (
    <Box p="4" style={{ maxHeight: "70vh", overflow: "auto" }}>
      <Text size="3" weight="medium" mb="3" style={{ display: "block" }}>
        Payload Templating
      </Text>
      <Text size="2" color="gray" mb="4" style={{ display: "block" }}>
        Use double curly braces <Code>{`{{ }}`}</Code> to reference dynamic
        values in your JSON payload. Templates support type preservation,
        meaning numbers stay as numbers, objects as objects, etc.
      </Text>

      <Box mb="4">
        <Text size="2" weight="medium" mb="2" style={{ display: "block" }}>
          Basic Syntax
        </Text>
        <Code size="1" style={{ display: "block", marginBottom: "8px" }}>
          {`{{ templateExpression }}`}
        </Code>
        <Code size="1" style={{ display: "block", marginBottom: "12px" }}>
          {`{{templateExpression}}`}
        </Code>
        <Text size="1" color="gray" style={{ display: "block" }}>
          Both spaced and unspaced formats are supported. Extra spaces are
          automatically trimmed.
        </Text>
      </Box>

      <Box mb="4">
        <Text size="2" weight="medium" mb="2" style={{ display: "block" }}>
          Trigger Data
        </Text>
        <Code size="1" style={{ display: "block", marginBottom: "8px" }}>
          {`{{ $anythink.trigger.id }}`}
        </Code>
        <Code size="1" style={{ display: "block", marginBottom: "8px" }}>
          {`{{ $anythink.trigger.data }}`}
        </Code>
        <Code size="1" style={{ display: "block", marginBottom: "12px" }}>
          {`{{ $anythink.trigger.data.fieldName }}`}
        </Code>
        <Text size="1" color="gray" style={{ display: "block" }}>
          Access data from the workflow trigger event. Use nested paths for
          accessing properties within the trigger data object.
        </Text>
      </Box>

      <Box mb="4">
        <Text size="2" weight="medium" mb="2" style={{ display: "block" }}>
          Previous Step Results
        </Text>
        <Code size="1" style={{ display: "block", marginBottom: "8px" }}>
          {`{{ $anythink.steps.stepKey }}`}
        </Code>
        <Code size="1" style={{ display: "block", marginBottom: "8px" }}>
          {`{{ $anythink.steps.stepKey.data }}`}
        </Code>
        <Code size="1" style={{ display: "block", marginBottom: "8px" }}>
          {`{{ $anythink.steps.stepKey.data[0].id }}`}
        </Code>
        <Code size="1" style={{ display: "block", marginBottom: "12px" }}>
          {`{{ $anythink.steps.stepKey.data[0].fieldName }}`}
        </Code>
        <Text size="1" color="gray" style={{ display: "block" }}>
          Access data from previous workflow steps using the step key. Use array
          notation to access specific items in result arrays.
        </Text>
      </Box>

      <Box mb="4">
        <Text size="2" weight="medium" mb="2" style={{ display: "block" }}>
          Current Item (Loop Context)
        </Text>
        <Code size="1" style={{ display: "block", marginBottom: "8px" }}>
          {`{{ fieldName }}`}
        </Code>
        <Code size="1" style={{ display: "block", marginBottom: "8px" }}>
          {`{{ nested.property }}`}
        </Code>
        <Text size="1" color="gray" mb="2" style={{ display: "block" }}>
          When processing arrays, access fields from the current item being
          processed. No prefix needed - just use the field name directly.
        </Text>
        <Text size="1" color="gray" style={{ display: "block" }}>
          Example: If iterating over users, <Code>{`{{ name }}`}</Code> accesses
          the current user's name field.
        </Text>
      </Box>

      <Box mb="4">
        <Text size="2" weight="medium" mb="2" style={{ display: "block" }}>
          Timestamp & Secrets
        </Text>
        <Code size="1" style={{ display: "block", marginBottom: "8px" }}>
          {`{{ $anythink.now }}`}
        </Code>
        <Code size="1" style={{ display: "block", marginBottom: "12px" }}>
          {`{{ $anythink.secrets.secretKey }}`}
        </Code>
        <Text size="1" color="gray" style={{ display: "block" }}>
          Use <Code>$anythink.now</Code> for the current UTC timestamp. Use{" "}
          <Code>$anythink.secrets</Code> to securely access stored secrets.
        </Text>
      </Box>

      <Box mb="4">
        <Text size="2" weight="medium" mb="2" style={{ display: "block" }}>
          Type Preservation
        </Text>
        <Text size="1" color="gray" mb="2" style={{ display: "block" }}>
          Templates preserve data types automatically:
        </Text>
        <Code size="1" style={{ display: "block", marginBottom: "8px" }}>
          {`"count": {{ $anythink.steps.stepKey.data[0].count }}`}
        </Code>
        <Text size="1" color="gray" mb="2" style={{ display: "block" }}>
          If the value is a number, it stays a number (no quotes needed). If
          it's a string, it stays a string. Objects and arrays are preserved
          as-is.
        </Text>
        <Text size="1" color="gray" style={{ display: "block" }}>
          When a template is the entire content of a quoted string, the type is
          preserved automatically.
        </Text>
      </Box>

      <Box>
        <Text size="2" weight="medium" mb="2" style={{ display: "block" }}>
          Example Payload
        </Text>
        <Code
          size="1"
          style={{
            display: "block",
            padding: "12px",
            backgroundColor: "var(--gray-2)",
            borderRadius: "var(--radius-2)",
            fontFamily: "monospace",
            whiteSpace: "pre",
            overflow: "auto",
          }}
        >
          {`{
  "name": "{{ $anythink.trigger.data.full_name }}",
  "email": "{{ $anythink.trigger.data.email }}",
  "department_id": {{ $anythink.steps.readDepartment.data[0].id }},
  "manager_id": {{ $anythink.steps.readDepartment.data.manager_id }},
  "created_at": "{{ $anythink.now }}",
  "metadata": {
    "source": "workflow",
    "trigger_id": {{ $anythink.trigger.id }}
  }
}`}
        </Code>
      </Box>

      <Box>
        <Text size="2" weight="medium" mb="2" style={{ display: "block" }}>
          Example Payload (Direct Pass-Through)
        </Text>
        <Code
          size="1"
          style={{
            display: "block",
            padding: "12px",
            backgroundColor: "var(--gray-2)",
            borderRadius: "var(--radius-2)",
            fontFamily: "monospace",
            whiteSpace: "pre",
            overflow: "auto",
          }}
        >
          {`{{$anythink.trigger.data}}`}
        </Code>
      </Box>
    </Box>
  );
};

interface PayloadHelpDialogProps {
  trigger?: React.ReactNode;
}

export default function PayloadHelpDialog({ trigger }: PayloadHelpDialogProps) {
  const [showHelp, setShowHelp] = useState(false);

  const defaultTrigger = (
    <IconButton
      type="button"
      variant="ghost"
      size="1"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setShowHelp(true);
      }}
      title="Help: Payload Templating"
      style={{ padding: "2px", cursor: "pointer" }}
    >
      <HelpCircle size={14} />
    </IconButton>
  );

  return (
    <>
      {trigger || defaultTrigger}
      <Modal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
        title="Payload Templating Help"
        size="lg"
      >
        <PayloadHelpContent />
      </Modal>
    </>
  );
}

export { PayloadHelpContent };
